import { apiClient, HttpError } from '@littlebird/auth-client';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

// Named constants for referencing specific section IDs (matching backend EchoNotesSections)
export const EchoNotesSections = {
  DEEP_USER_MODEL: 'deep_user_model',
  MEMORY_GAPS: 'memory_gaps',
  TOPIC_HIGHLIGHTS: 'topic_highlights',
  USER_INSIGHTS: 'user_insights',
  CUSTOM_MEMORY_DOC: 'custom_memory_doc',
  WORK_PROFILE: 'work_profile',
} as const;

export type EchoNotesSection =
  (typeof EchoNotesSections)[keyof typeof EchoNotesSections];

// Echonotes tabs plus collector tabs the Memory viewer shows together.
export const ECHONOTES_SECTION_DEFS: readonly {
  section: EchoNotesSection;
  tabValue: string;
  label: string;
  maxLength: number;
}[] = [
  {
    section: EchoNotesSections.USER_INSIGHTS,
    tabValue: 'user-insights',
    label: 'User Insights',
    maxLength: 5_000,
  },
  {
    section: EchoNotesSections.TOPIC_HIGHLIGHTS,
    tabValue: 'topics',
    label: 'Topic Highlights',
    maxLength: 3_000,
  },
  {
    section: EchoNotesSections.DEEP_USER_MODEL,
    tabValue: 'deep-model',
    label: 'Deep User Model',
    maxLength: 8_000,
  },
  {
    section: EchoNotesSections.MEMORY_GAPS,
    tabValue: 'memory-gaps',
    label: 'Agentic Memory',
    maxLength: 20_000,
  },
  {
    section: EchoNotesSections.CUSTOM_MEMORY_DOC,
    tabValue: 'custom-memory-doc',
    label: 'Custom Memory Doc',
    maxLength: 10_000,
  },
  {
    section: EchoNotesSections.WORK_PROFILE,
    tabValue: 'work-profile',
    label: 'Work Profile',
    maxLength: 50_000,
  },
];

// Schema matching backend EchoNotesSchema
const EchoNotesSchema = z.object({
  id: z.string(),
  full_text: z.string(),
  latest_included_message_timestamp: z.string(),
});

const EchoNotesTimestampsSchema = z.object({
  id: z.string(),
  latest_included_message_timestamp: z.string(),
});

const GetLatestEchoNotesSchema = z.object({
  latest_echonotes: EchoNotesSchema.nullable(),
  echonotes_timestamps: z.array(EchoNotesTimestampsSchema),
});
export type EchoNotesTimestamps = z.infer<typeof EchoNotesTimestampsSchema>;

export const ECHONOTES_KEYS = {
  all: ['echonotes'] as const,
  latest: (section?: EchoNotesSection) =>
    [...ECHONOTES_KEYS.all, 'latest', section] as const,
  detail: (id: string, section?: EchoNotesSection) =>
    [...ECHONOTES_KEYS.all, id, section] as const,
};

export const getLatestEchoNotesQuery = (
  section: EchoNotesSection = EchoNotesSections.USER_INSIGHTS
) =>
  queryOptions({
    queryKey: ECHONOTES_KEYS.latest(section),
    retry: 3,
    queryFn: async ({ signal }) => {
      try {
        const url = `echonotes/latest?section=${section}`;
        const data = await apiClient.get(url, { signal });
        return GetLatestEchoNotesSchema.parse(data);
      } catch (error) {
        if (error instanceof HttpError && error.status === 403) {
          throw new Error('This section is not enabled');
        }
        throw error instanceof HttpError
          ? new Error('Failed to fetch latest document')
          : error;
      }
    },
  });

export const getEchoNotesQuery = (
  echonotesId: string,
  section: EchoNotesSection = EchoNotesSections.USER_INSIGHTS
) =>
  queryOptions({
    queryKey: ECHONOTES_KEYS.detail(echonotesId, section),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: 3,
    queryFn: async ({ signal }) => {
      const url = `echonotes/${echonotesId}?section=${section}`;
      const data = await apiClient.get(url, { signal });
      return EchoNotesSchema.parse(data);
    },
  });
